import { Bike, Edit, Eye, Filter, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './Bicicletas.css';

const Bicicletas = () => {
  const { authenticatedFetch } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    modelId: '',
    serialNumber: '',
    size: 'M',
    currentStatus: 'AVAILABLE'
  });



  const vehicleSizes = [
    { value: 'XS', label: 'XS' },
    { value: 'S', label: 'S' },
    { value: 'M', label: 'M' },
    { value: 'ML', label: 'ML' },
    { value: 'L', label: 'L' },
    { value: 'XL', label: 'XL' },
    { value: 'XXL', label: 'XXL' }
  ];

  const vehicleStatuses = [
    { value: 'AVAILABLE', label: 'Disponible', color: 'available' },
    { value: 'IN_USE', label: 'En uso', color: 'in-use' },
    { value: 'MAINTENANCE', label: 'Mantenimiento', color: 'maintenance' },
    { value: 'REMOVED', label: 'Eliminada', color: 'removed' }
  ];

  const modelById = (id) =>
    models.find((m) => String(m.id) === String(id));

  const isCarritoModel = (model) => model?.category?.businessGroup === 'CARRITOS';

  const selectedModel = useMemo(
    () => models.find((m) => String(m.id) === String(formData.modelId)),
    [models, formData.modelId]
  );
  const isCarritoSelected = isCarritoModel(selectedModel);

  useEffect(() => {
    fetchVehicles();
    fetchModels();
  }, []);

  // Filtrar vehículos cuando cambie el término de búsqueda
  useEffect(() => {
    const filtered = vehicles.filter(vehicle =>
      vehicle.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.brand.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredVehicles(filtered);
    setCurrentPage(1);
  }, [vehicles, searchTerm]);

  // Calcular vehículos para la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentVehicles = filteredVehicles.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando vehículos desde:', `${API_BASE_URL}/api/vehicles`);
      
      const response = await authenticatedFetch(`${API_BASE_URL}/api/vehicles`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Vehículos cargados:', data);
        setVehicles(data);
      } else {
        console.error('❌ Error al cargar vehículos:', response.status, response.statusText);
        setError(`Error al cargar vehículos: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      setError('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/vehicle-models`);
      if (response.ok) {
        const data = await response.json();
        setModels(data);
      }
    } catch (error) {
      console.error('Error al cargar modelos:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      modelId: '',
      serialNumber: '',
      size: 'M',
      currentStatus: 'AVAILABLE'
    });
    setEditingVehicle(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const model = modelById(formData.modelId);
    const submitData = {
      modelId: parseInt(formData.modelId, 10),
      serialNumber: formData.serialNumber,
      currentStatus: formData.currentStatus,
      // La BD exige enum VehicleSize; los carritos no usan talla en negocio → valor por defecto fijo.
      size: isCarritoModel(model) ? 'M' : formData.size,
    };

    try {
      const url = editingVehicle 
        ? buildApiUrl(`${API_ENDPOINTS.VEHICLES}/${editingVehicle.id}`)
        : buildApiUrl(API_ENDPOINTS.VEHICLES);
      
      console.log('🔄 Enviando datos a:', url);
      console.log('📦 Datos:', submitData);
      
      const response = await authenticatedFetch(url, {
        method: editingVehicle ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Vehículo guardado exitosamente:', result);
        await fetchVehicles();
        setShowForm(false);
        resetForm();
        alert(editingVehicle ? 'Vehículo actualizado exitosamente' : 'Vehículo creado exitosamente');
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        
        if (response.status === 401) {
          alert('Error: No tienes permisos para realizar esta acción. Verifica tu autenticación.');
        } else if (response.status === 403) {
          alert('Error: Acceso denegado. No tienes permisos suficientes.');
        } else {
          alert(`Error: ${errorData.message || 'Error al guardar el vehículo'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error de conexión:', error);
      alert('Error de conexión al servidor. Verifica que el backend esté ejecutándose.');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      modelId: vehicle.modelId.toString(),
      serialNumber: vehicle.serialNumber,
      size: vehicle.size,
      currentStatus: vehicle.currentStatus
    });
    setShowForm(true);
  };

  const handleView = async (vehicleId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/vehicles/${vehicleId}`);
      if (response.ok) {
        const vehicle = await response.json();
        setViewingVehicle(vehicle);
      }
    } catch (error) {
      console.error('Error al cargar detalles del vehículo:', error);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(buildApiUrl(`${API_ENDPOINTS.VEHICLES}/${vehicleId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchVehicles();
        alert('Vehículo eliminado exitosamente');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Error al eliminar el vehículo'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar el vehículo');
    }
  };

  const getStatusLabel = (status) => {
    const statusObj = vehicleStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusColor = (status) => {
    const statusObj = vehicleStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : 'default';
  };

  if (loading) {
    return (
      <div className="bicicletas-page">
        <div className="loading">Cargando vehículos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bicicletas-page">
        <div className="error-message">
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            className="btn-primary"
            onClick={() => {
              setError(null);
              fetchVehicles();
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bicicletas-page">
      <div className="page-header">
        <h1>Gestión de vehículos</h1>
        <button 
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus size={16} />
          Nuevo vehículo
        </button>
      </div>

      {/* Buscador y controles */}
      <div className="table-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por número de serie, modelo o marca..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="pagination-controls">
          <label>
            Mostrar:
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-per-page-select"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
        </div>
      </div>

      <div className="table-container">
        {filteredVehicles.length === 0 ? (
          <div className="no-vehicles">
            {searchTerm ? (
              <p>No se encontraron vehículos que coincidan con "{searchTerm}"</p>
            ) : (
              <p>No hay vehículos registrados</p>
            )}
            {!searchTerm && (
              <button 
                className="btn-primary"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
              >
                Crear el primer vehículo
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Número de Serie</th>
                  <th>Modelo</th>
                  <th>Marca</th>
                  <th>Talla</th>
                  <th>Estado</th>
                  {/* <th>Modificador de Precio</th> */}
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentVehicles.map((vehicle) => (
                  <tr key={vehicle.id}>
                    <td>#{vehicle.id}</td>
                    <td>{vehicle.serialNumber}</td>
                    <td>{vehicle.model?.name || 'N/A'}</td>
                    <td>{vehicle.model?.brand || 'N/A'}</td>
                    <td>
                      {isCarritoModel(modelById(vehicle.modelId)) ? '—' : vehicle.size}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(vehicle.currentStatus)}`}>
                        {getStatusLabel(vehicle.currentStatus)}
                      </span>
                    </td>
                    {/* <td>€{vehicle.priceModifier}</td> */}
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="Ver detalles"
                          onClick={() => handleView(vehicle.id)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn-icon"
                          title="Editar"
                          onClick={() => handleEdit(vehicle)}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          title="Eliminar"
                          onClick={() => handleDelete(vehicle.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}

            {/* Información de resultados */}
            <div className="results-info">
              Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredVehicles.length)} de {filteredVehicles.length} vehículos
            </div>
          </>
        )}
      </div>

      {/* Formulario Modal */}
      {showForm && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>{editingVehicle ? 'Editar vehículo' : 'Nuevo vehículo'}</h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="vehicle-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Modelo *</label>
                  <select
                    name="modelId"
                    value={formData.modelId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar modelo</option>
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} — {model.brand}
                        {isCarritoModel(model) ? ' (carrito)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Número de Serie *</label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: SN123456789"
                  />
                </div>
              </div>

              <div className="form-row">
                {!isCarritoSelected && (
                  <div className="form-group">
                    <label>Talla</label>
                    <select
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                    >
                      {vehicleSizes.map((size) => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    name="currentStatus"
                    value={formData.currentStatus}
                    onChange={handleInputChange}
                  >
                    {vehicleStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* <div className="form-group">
                <label>Modificador de Precio (€)</label>
                <input
                  type="number"
                  name="priceModifier"
                  value={formData.priceModifier}
                  onChange={handleInputChange}
                  step="0.01"
                  placeholder="0.00"
                />
                <small>Precio adicional o descuento sobre el precio base del modelo</small>
              </div> */}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingVehicle ? 'Actualizar' : 'Crear'} vehículo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Detalles */}
      {viewingVehicle && (
        <div className="form-overlay">
          <div className="form-container">
            <div className="form-header">
              <h2>Detalles del vehículo</h2>
              <button 
                className="btn-close"
                onClick={() => setViewingVehicle(null)}
              >
                ×
              </button>
            </div>

            <div className="vehicle-details">
              <div className="detail-row">
                <strong>ID:</strong> #{viewingVehicle.id}
              </div>
              <div className="detail-row">
                <strong>Número de Serie:</strong> {viewingVehicle.serialNumber}
              </div>
              <div className="detail-row">
                <strong>Modelo:</strong> {viewingVehicle.model?.name || 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Marca:</strong> {viewingVehicle.model?.brand || 'N/A'}
              </div>
              {isCarritoModel(modelById(viewingVehicle.modelId)) ? (
                <div className="detail-row">
                  <strong>Talla:</strong> no aplica (carrito)
                </div>
              ) : (
                <div className="detail-row">
                  <strong>Talla:</strong> {viewingVehicle.size}
                </div>
              )}
              <div className="detail-row">
                <strong>Estado:</strong> 
                <span className={`status-badge ${getStatusColor(viewingVehicle.currentStatus)}`}>
                  {getStatusLabel(viewingVehicle.currentStatus)}
                </span>
              </div>
              {/* <div className="detail-row">
                <strong>Modificador de Precio:</strong> €{viewingVehicle.priceModifier}
              </div> */}
              <div className="detail-row">
                <strong>Precio Base del Modelo:</strong> €{viewingVehicle.model?.basePrice || 'N/A'}
              </div>
              <div className="detail-row">
                <strong>Fecha de Creación:</strong> {new Date(viewingVehicle.createdAt).toLocaleDateString()}
              </div>
              <div className="detail-row">
                <strong>Última Actualización:</strong> {new Date(viewingVehicle.updatedAt).toLocaleDateString()}
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setViewingVehicle(null)}
              >
                Cerrar
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setViewingVehicle(null);
                  handleEdit(viewingVehicle);
                }}
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bicicletas; 